import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Paper,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import MathRenderer from '../common/MathRenderer';
import AIGeneratedQuestionCard, { getQuestionTypeLabel } from './AIGeneratedQuestionCard';
import AIPreviewQuestionDialog from './AIPreviewQuestionDialog';
import { QUESTIONS_AI_GENERATE, QUESTIONS_BULK_CREATE } from '../../config/Config';

const AIGenerateDialog = ({
  open,
  onClose,
  sourceQuestion,
  difficulties = [],
  onSuccess,
  onError,
}) => {
  const [difficulty, setDifficulty] = useState('Basic');
  const [count, setCount] = useState(3);
  const [noteToAI, setNoteToAI] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setDifficulty('Basic');
      setCount(3);
      setNoteToAI('');
      setLoading(false);
      setError('');
      setCandidates([]);
      setSelectedCandidates([]);
      setShowPreview(false);
      setPreviewQuestion(null);
    }
  }, [open]);

  // Set default difficulty based on source question or first available difficulty
  useEffect(() => {
    if (sourceQuestion?.difficulty) {
      setDifficulty(sourceQuestion.difficulty);
    } else if (difficulties.length > 0) {
      setDifficulty(difficulties[0].name);
    }
  }, [sourceQuestion, difficulties]);

  const handleGenerate = async () => {
    if (!sourceQuestion) {
      setError('No source question provided');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(QUESTIONS_AI_GENERATE, {
        source_question: sourceQuestion,
        difficulty: difficulty,
        count: count,
        note_to_ai: noteToAI.trim() || null,
      });

      const generatedCandidates = response.data.candidates || [];
      setCandidates(generatedCandidates);
      setSelectedCandidates(generatedCandidates.map((_, index) => index));
      setShowPreview(true);
    } catch (err) {
      console.error('AI generation error:', err);
      
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else if (err.response?.status === 408) {
        setError('Request timed out. Please try again.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to generate questions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateToggle = (index) => {
    setSelectedCandidates(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleSaveSelected = async () => {
    if (selectedCandidates.length === 0) {
      setError('Please select at least one question to save');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const questionsToSave = selectedCandidates.map(index => candidates[index]);
      
      const response = await api.post(QUESTIONS_BULK_CREATE, {
        questions: questionsToSave,
      });

      const createdIds = response.data.created_ids || [];
      
      onSuccess?.(createdIds.length);
      onClose();
    } catch (err) {
      console.error('Bulk create error:', err);
      
      // Handle detailed error messages from backend
      if (err.response?.data?.questions) {
        // This is a validation error with specific question details
        const errorMsg = Array.isArray(err.response.data.questions) 
          ? err.response.data.questions.join('; ') 
          : err.response.data.questions;
        setError(errorMsg);
      } else if (err.response?.data?.error) {
        // Generic error message
        setError(err.response.data.error);
      } else if (err.response?.data) {
        // Try to extract any error message from response
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          setError(errorData);
        } else if (errorData.detail) {
          setError(errorData.detail);
        } else {
          setError('Failed to save questions. Please try again.');
        }
      } else {
        setError('Failed to save questions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    setShowPreview(false);
    setCandidates([]);
    setSelectedCandidates([]);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon color="secondary" />
        Generate Similar Questions (AI)
        <Button
          onClick={onClose}
          sx={{ ml: 'auto' }}
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent>
        {!showPreview ? (
          <>
            {/* Source Question Display */}
            {sourceQuestion && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Source Question
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip 
                      label={getQuestionTypeLabel(sourceQuestion.question_type)} 
                      size="small" 
                    />
                    {sourceQuestion.difficulty && (
                      <Chip 
                        label={sourceQuestion.difficulty} 
                        size="small" 
                        color="primary"
                      />
                    )}
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <MathRenderer content={sourceQuestion.question_text} />
                  </Typography>
                  {sourceQuestion.subject_name && (
                    <Typography variant="body2" color="text.secondary">
                      Subject: {sourceQuestion.subject_name} | Topic: {sourceQuestion.topic_name}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Generation Settings */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Generation Settings
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={difficulty}
                  label="Difficulty"
                  onChange={(e) => setDifficulty(e.target.value)}
                >
                  {difficulties.map((diff) => (
                    <MenuItem key={diff.id} value={diff.name}>
                      {diff.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Number of Questions"
                type="number"
                value={count}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setCount(1);
                    return;
                  }
                  const num = Math.round(Number(raw));
                  if (!Number.isFinite(num)) return;
                  const clamped = Math.min(5, Math.max(1, num));
                  setCount(clamped);
                }}
                onKeyDown={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                inputProps={{ min: 1, max: 5, step: 1 }}
                helperText="1-5 questions (use arrows to change)"
                fullWidth
              />
            </Box>

            {/* Note to AI Field */}
            <TextField
              label="Note to AI (Optional)"
              value={noteToAI}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 250) {
                  setNoteToAI(value);
                }
              }}
              multiline
              rows={2}
              fullWidth
              helperText={`${noteToAI.length}/250 characters. Key instruction (shown to AI above the question pattern) for generation.`}
              placeholder="e.g., 'Focus on real-world applications' or 'Use larger numbers'"
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </>
        ) : (
          <>
            {/* Preview Generated Questions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Generated Questions ({candidates.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCandidates.length} of {candidates.length} selected
              </Typography>
            </Box>

            {candidates.map((question, index) => (
              <AIGeneratedQuestionCard
                key={index}
                question={question}
                index={index}
                isSelected={selectedCandidates.includes(index)}
                onToggle={handleCandidateToggle}
                onViewPreview={setPreviewQuestion}
                showDetailAnswer
              />
            ))}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 1.5, sm: 2 },
          gap: 1,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          '& > button': {
            minHeight: 42,
            flex: { xs: '1 1 0', sm: '0 0 auto' },
            whiteSpace: 'nowrap',
          },
        }}
      >
        {!showPreview ? (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
            >
              {loading ? 'Generating...' : 'Generate Questions'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleDiscard} disabled={loading}>
              Discard
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowPreview(false)}
              disabled={loading}
            >
              Back to Settings
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveSelected}
              disabled={loading || selectedCandidates.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              sx={{
                flex: { xs: '0 0 auto', sm: '0 0 auto' },
                minWidth: { xs: 118, sm: 150 },
                px: { xs: 2, sm: 2.5 },
                '& .MuiButton-startIcon': { mr: 0.75 },
              }}
            >
              {loading ? 'Saving...' : `Save(${selectedCandidates.length})`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    <AIPreviewQuestionDialog
      open={Boolean(previewQuestion)}
      onClose={() => setPreviewQuestion(null)}
      question={previewQuestion}
    />
    </>
  );
};

export default AIGenerateDialog;
